import type { IconName } from "~/utils/icons";

/**
 * What one entity's ⋯ menu offers, as a pure function of its kind and the status word it wears.
 *
 * It lives beside the component rather than inside it because two surfaces have to agree on one
 * rule: the menu that offers an action, and the dialog that carries out the action the menu
 * offered. When they disagreed, the portal offered Delete on a version the server would refuse
 * and offered no way to reach the state that makes deletion legal (W8-WALK §10).
 */
export interface EntityMenuItem {
  id: string;
  label: string;
  icon: IconName;
}

/** A run that is still going: it is stopped, not archived and not deleted. */
export const MODEL_RUNNING_STATES = ["Training", "Preparing", "Queued", "Waiting", "In progress"];

/**
 * The two model rules the server enforces, stated once in the portal's own words.
 *
 * Archive: the API takes `{archived:true}` only on a version whose run succeeded — the word the
 * portal shows for that is `Ready`. Delete: the API takes a version that failed, was stopped, or
 * has been archived. So archiving is the one step that turns a finished, undeletable version into
 * a deletable one, and `Restore` is what keeps it from being a one-way door.
 */
export const modelArchivable = (status?: string): boolean => status === "Ready";
export const modelRestorable = (status?: string): boolean => status === "Archived";
export const modelDeletable = (status?: string): boolean =>
  status === "Archived" || status === "Failed" || status === "Stopped";

/**
 * A project wears the same word when it is out of use, and the API takes `{archived:false}` on it
 * unconditionally — there is no state a restore is refused from.
 */
export const projectRestorable = (status?: string): boolean => status === "Archived";

export function entityMenuItems(entity: { kind: string; status?: string }): EntityMenuItem[] {
  const { kind } = entity;
  const status = entity.status ?? "";
  const items: EntityMenuItem[] = [];
  if (!["key", "connector", "member", "invitation", "organisation"].includes(kind)) items.push({ id: "open", label: "Open", icon: "eye" });
  if (["dataset", "memory", "project", "deployment"].includes(kind)) items.push({ id: "rename", label: "Rename", icon: "pen" });
  if (kind === "data") {
    items.push({ id: "replace", label: "Replace file", icon: "upload" });
    /* The item says what it does: it puts the document into a named collection (owner,
       2026-09-22). A document is ready once its upload has finished. */
    if (status === "Ready") items.push({ id: "add-to-memory", label: "Add to a collection", icon: "memory" });
  }
  if (kind === "dataset") {
    /* No build action: preparation is the first stage of training and is pressed there, not as a
       step of its own on the dataset (founder, 2026-09-21). A source keeps its own schedule on
       the dataset page, so that is not a dataset-wide operation either. */
    items.push({ id: "dataset-files", label: "Add files", icon: "plus" }, { id: "dataset-source", label: "Attach source", icon: "plug" }, { id: "train", label: "Train", icon: "play" }, { id: "download-dataset", label: "Download", icon: "download" });
  }
  if (kind === "connector") items.push({ id: "connector-dataset", label: "Import", icon: "datasets" }, { id: "connector-credentials", label: "Rotate credentials", icon: "key" });
  if (kind === "member") items.push({ id: "member-role", label: "Change role", icon: "shield" }, { id: "member-remove", label: "Remove", icon: "trash" });
  if (kind === "invitation") items.push({ id: "invitation-cancel", label: "Cancel invitation", icon: "x" });
  if (kind === "organisation") items.push({ id: "org-rename", label: "Rename", icon: "pen" });
  if (kind === "model") {
    if (MODEL_RUNNING_STATES.includes(status)) items.push({ id: "cancel", label: "Cancel run", icon: "x" });
    else if (modelRestorable(status)) {
      /* An archived version is out of use: the server refuses to publish it and it answers
         nothing, so neither Deploy nor Playground is offered. What it still has is its files —
         and the way back. */
      items.push({ id: "restore-model", label: "Restore", icon: "refresh" }, { id: "export", label: "Download", icon: "download" });
    } else {
      items.push({ id: "playground", label: "Playground", icon: "playground" }, { id: "deploy", label: "Deploy", icon: "deploy" }, { id: "export", label: "Download", icon: "download" });
      if (modelArchivable(status)) items.push({ id: "archive-model", label: "Archive", icon: "archive" });
    }
  }
  if (kind === "deployment") {
    items.push({ id: "deploy", label: "New revision", icon: "plus" }, { id: "deployment-memory", label: "Choose collections", icon: "memory" });
    if (status !== "Paused" && status !== "Offline") items.push({ id: "deployment-offline", label: "Take offline", icon: "x" });
  }
  /* A project's lifecycle is the same pair as the model version's, and the contract says so:
     `{archived:true}` hides it from the active list and the switcher, `{archived:false}` puts it
     back, and the server takes the second on any project it can still resolve. The organisation's
     project list reads `/projects?includeArchived=true`, so an archived project is on screen there
     wearing the word `Archived`, with the same ⋯ on its row and in the inspector. Before this,
     that menu offered the archiving the project had already had, and nothing undid it
     (W8-DS-23). */
  if (kind === "project") items.push(projectRestorable(status) ? { id: "restore-project", label: "Restore", icon: "refresh" } : { id: "archive-project", label: "Archive", icon: "archive" });
  else if (kind === "key") items.push({ id: "revoke", label: "Revoke", icon: "key" });
  /* A run that is still going has nothing to delete yet; the action that applies to it is the one
     above it, Cancel run (founder, 2026-09-21, note 31). Every settled version keeps Delete, and
     when the version is not deletable yet the dialog behind it offers the step that makes it so. */
  else if (kind === "model") { if (!MODEL_RUNNING_STATES.includes(status)) items.push({ id: "delete", label: "Delete", icon: "trash" }); }
  else if (!["member", "invitation", "organisation"].includes(kind)) items.push({ id: "delete", label: "Delete", icon: "trash" });
  return items;
}
